import { type FC } from "react";
import { View, Text, Flex } from "@aws-amplify/ui-react";

// Rating levels configuration with colors
const ratingLevels = [
  { number: 0, color: "#db2828" }, // Red
  { number: 1, color: "#f2711c" }, // Orange
  { number: 2, color: "#fbbd08" }, // Yellow
  { number: 3, color: "#767676" }, // Gray
  { number: 4, color: "#b5cc18" }, // Light green
  { number: 5, color: "#21ba45" }, // Green
];

interface SkillRatingCircleProps {
  percentage?: number;
  rating?: number | null;
  size?: "small" | "medium" | "large";
  showLabel?: boolean; // NEW: Option to show "Proficiency" label
  compact?: boolean; // NEW: Compact mode with smaller percentage text
}

export const SkillRatingCircle: FC<SkillRatingCircleProps> = ({
  percentage,
  rating,
  size = "medium",
  showLabel = false,
  compact = false,
}) => {
  // Handle null/undefined ratings (not rated)
  const isRated = rating !== null && rating !== undefined && rating > 0;
  let validPercentage: number;

  if (rating !== undefined && rating !== null) {
    // Convert rating (1-5) to percentage (20-100), 0 or null = 0%
    validPercentage = isRated ? (rating / 5) * 100 : 0;
  } else {
    // Ensure percentage is within valid range
    validPercentage = Math.min(Math.max(0, percentage || 0), 100);
  }

  // Get color based on percentage ranges (0-20%, 20-40%, etc.)
  const percentageLevel = Math.floor(validPercentage / 20);
  const color = isRated
    ? ratingLevels[percentageLevel]?.color || "#db2828"
    : "#f0f0f0";

  // Determine size in rem
  let sizeInRem: string;
  let fontSize: string;
  switch (size) {
    case "small":
      sizeInRem = "2rem";
      fontSize = compact ? "0.65rem" : "0.7rem";
      break;
    case "large":
      sizeInRem = "4rem";
      fontSize = compact ? "1.1rem" : "1.4rem";
      break;
    case "medium":
    default:
      sizeInRem = "3rem";
      fontSize = compact ? "0.85rem" : "1.05rem";
  }

  const circleContent = (
    <View
      style={{
        width: sizeInRem,
        height: sizeInRem,
        borderRadius: "50%",
        backgroundColor: color,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        boxShadow: "0 0 4px rgba(0,0,0,0.2)",
        position: "relative",
      }}
      title={
        rating !== undefined && rating !== null
          ? `Rating: ${isRated ? rating : "Not rated"} out of 5`
          : `Proficiency: ${Math.round(validPercentage)}%`
      }
    >
      <Text
        color={isRated ? "white" : "#666"}
        fontWeight="bold"
        fontSize={fontSize}
      >
        {rating !== undefined && rating !== null
          ? isRated
            ? rating
            : "N/A"
          : `${Math.round(validPercentage)}%`}
      </Text>
    </View>
  );

  // If showLabel is true, wrap with label
  if (showLabel && rating === undefined) {
    return (
      <Flex direction="column" alignItems="center" gap="0.25rem">
        {circleContent}
        <Text fontSize="0.75rem" color="var(--amplify-colors-font-secondary)">
          Proficiency
        </Text>
      </Flex>
    );
  }

  return circleContent;
};

export default SkillRatingCircle;
